/**
 * Keeping several TrendPulse curves on one chart.
 *
 * <h2>What changed and why</h2>
 *
 * A TrendPulse run used to delete the previous curve before drawing its own, so the study could
 * only ever answer "what does this preset look like" and never "which of these presets fits this
 * symbol better" — which is the question a smoothing parameter exists to be asked.
 *
 * Curves are now kept, up to {@link MAX_TRENDPULSE_OVERLAYS}, and the oldest is evicted beyond
 * that. A cap rather than an unbounded list because each curve is one value per bar: four on a
 * twenty-year daily chart is already twenty thousand points, and past four the lines are also
 * unreadable, which is the more immediate limit.
 */

/** How many TrendPulse curves may share a chart. */
export const MAX_TRENDPULSE_OVERLAYS = 4;

export const TRENDPULSE_OVERLAY_TYPE = 'astroGraphLine';

/**
 * Pens for the curves, in assignment order.
 *
 * Colour AND stroke differ on every entry rather than colour alone. Four lines tracing similar
 * shapes across the same panel are told apart by their pattern where they overlap and by their
 * colour where they do not, and a reader who cannot distinguish red from green still has four
 * distinguishable curves.
 */
export const TRENDPULSE_PENS = [
  { color: '#ff6600', lineType: 'solid' },
  { color: '#1976d2', lineType: 'dashed' },
  { color: '#2e7d32', lineType: 'dotted' },
  { color: '#8e24aa', lineType: 'dashdot' },
];

/** The TrendPulse curves currently on the chart, oldest first. */
export function existingTrendPulseOverlays(overlays) {
  return (Array.isArray(overlays) ? overlays : [])
    .filter(o => o && o.type === TRENDPULSE_OVERLAY_TYPE);
}

/**
 * The pen for the next curve.
 *
 * Picks the first pen no curve on the chart is already using, so removing the second of four and
 * adding another reuses the freed pen rather than skipping to a fifth that does not exist. Falls
 * back to position when every pen is taken.
 */
export function nextTrendPulsePen(overlays) {
  const taken = new Set(existingTrendPulseOverlays(overlays).map(o => o.color));
  const free = TRENDPULSE_PENS.find(pen => !taken.has(pen.color));
  return free || TRENDPULSE_PENS[existingTrendPulseOverlays(overlays).length % TRENDPULSE_PENS.length];
}

/**
 * Which existing curves must go to make room for one more.
 *
 * Returns the ids to remove, oldest first. Empty while there is room.
 */
export function trendPulseOverlaysToEvict(overlays, adding = 1) {
  const existing = existingTrendPulseOverlays(overlays);
  const surplus = existing.length + adding - MAX_TRENDPULSE_OVERLAYS;
  if (surplus <= 0) return [];
  return existing.slice(0, surplus).map(o => o.id);
}

/**
 * The overlay for a new TrendPulse run.
 *
 * The label carries the preset because it is drawn at the end of the line as the legend, and four
 * curves all reading "TrendPulse" would be a legend that identifies nothing.
 */
export function buildTrendPulseOverlay({ series, preset, overlays, color, thickness, lineType }) {
  const pen = nextTrendPulsePen(overlays);
  const presetLabel = preset
    ? preset.charAt(0).toUpperCase() + preset.slice(1)
    : 'Custom';

  return {
    // Unique per run: several curves coexist, so a shared id would make them one overlay.
    id: `astrograph-${preset || 'custom'}-${Date.now()}`,
    type: TRENDPULSE_OVERLAY_TYPE,
    label: `TrendPulse (${presetLabel})`,
    preset: preset || null,
    // An explicit choice wins; otherwise the next free pen keeps the curves distinguishable
    // without the customer having to pick a colour they have not already used.
    color: color || pen.color,
    thickness: thickness || 2,
    lineType: lineType || pen.lineType,
    data: series,
  };
}
